import {
    deepCopy,
    DisplayValue,
    DynamicTableColumn,
    ECONode,
    Organization,
    OrgHat,
    OrgPart,
    TableAction,
    Positions,
    OrgSecurityGroup,
} from '@newgenus/common';
import { SearchableSelectComponent } from '../../../searchable-select/searchable-select.component';
import { OrgFilterBarComponent, OrgFilterConfig } from '../../shared/org-filter-bar/org-filter-bar.component';
import { OrgSearchBarComponent, PositionSearchResult } from '../../shared/org-search-bar/org-search-bar.component';
import { fuzzyMatch } from '../../shared/org-filter-utils';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { OrgBoardBuilderService } from '../../services/org-board-builder.service';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { OrgDialogService } from '../../services/org-dialog.service';
import { OrgBoardService } from '../../services/org-board-service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Component, inject, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatInputModule } from '@angular/material/input';
import { MatRippleModule } from '@angular/material/core';
import { SlicePipe } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { DialogService } from '../../../../services/dialog.service';
import { OrgSecurityGroupDetailsDialogComponent } from '../org-security-group-details-dialog/org-security-group-details-dialog';
import { OrgPositionDetailsDialogComponent } from '../org-position-details-dialog/org-position-details-dialog';
import { OrgPolicyDetailsDialogComponent } from '../org-policy-details-dialog/org-policy-details-dialog';
import { OrgUserDetailsDialogComponent } from '../org-user-details-dialog';
import { OrgHatExplorerDialogComponent } from '../org-hat-explorer-dialog/org-hat-explorer-dialog';
import { OrgHatDetailsDialogComponent } from '../org-hat-details-dialog/org-hat-details-dialog';
import { OrgHatCleanupDialogComponent } from '../org-hat-cleanup-dialog/org-hat-cleanup-dialog';
import {
    OrgItemSelectorDialogComponent,
    ItemSelectorDialogResult,
    ItemSelectorType,
} from '../org-item-selector-dialog/org-item-selector-dialog';
import { ProfileColorService } from '../../services/profile-color.service';
import { OrgHattingService } from '../../services/org-hatting.service';

@Component({
    selector: 'shared-org-new-hat-dialog',
    imports: [
        SlicePipe,
        ReactiveFormsModule,
        SearchableSelectComponent,
        OrgFilterBarComponent,
        OrgSearchBarComponent,
        MatMenuModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatTabsModule,
        MatTableModule,
        MatDialogModule,
        MatInputModule,
        MatSlideToggleModule,
        MatRippleModule,
        MatProgressBarModule,
        MatTooltipModule,
        MatChipsModule,
        MatBadgeModule,
    ],
    templateUrl: './org-new-hat-dialog.html',
    styleUrls: ['./org-new-hat-dialog.scss', '../org-dialog.scss', '../../organization-board.component.scss'],
})
export class OrgNewHatDialogComponent implements OnInit, OnDestroy {
    //#region Variables
    // Variables for this component that are within scope of "this" component.

    public form = new FormGroup({
        description: new FormControl<string | null>(null),
        isMultiUsers: new FormControl<boolean | null>(false),
        isSpecialRights: new FormControl<boolean | null>(false),
        key: new FormControl<string | null>(null),
        name: new FormControl<string | null>(null, Validators.compose([Validators.required, Validators.minLength(3)])),
        policies: new FormControl<string[] | null>(null),
        positionArray: new FormControl<string[] | null>(null),
        securityGroupsArray: new FormControl<string[] | null>(null),
        assignedToArray: new FormControl<string[] | null>([]),
    });

    public columns: DynamicTableColumn<OrgHat>[] = [
        {
            columnDef: 'name',
            header: 'Name',
            cell: (element: OrgHat) => element.name || '',
        },
        {
            columnDef: 'description',
            header: 'Description',
            cell: (element: OrgHat) =>
                element.description
                    ? element.description.length > 60
                        ? element.description.substring(0, 60) + '...'
                        : element.description
                    : '',
        },
        {
            columnDef: 'users',
            header: 'Users',
            cell: (element: OrgHat) => {
                const hatData = element as any;
                const users = hatData.assignedToArray || element.userKeys || [];
                return users.length ? `${users.length} user${users.length > 1 ? 's' : ''}` : 'None';
            },
        },
        {
            columnDef: 'securityGroups',
            header: 'Security Groups',
            cell: (element: OrgHat) => {
                const sgs = element.securityGroupsArray || element.securityGroups || [];
                return sgs.length ? `${sgs.length} group${sgs.length > 1 ? 's' : ''}` : 'None';
            },
        },
        {
            columnDef: 'positions',
            header: 'Scope',
            cell: (element: OrgHat) => {
                if (element.isSpecialRights) return 'Special Rights';
                const hatData = element as any;
                const positions = hatData.positionArray || element.positions?.map((p: any) => p.key || p.partKey) || [];
                return positions.length ? `${positions.length} position${positions.length > 1 ? 's' : ''}` : 'Org-wide';
            },
        },
        {
            columnDef: 'actions',
            header: '',
            cell: () => '',
        },
    ];
    public columnActions: TableAction<OrgHat>[] = [
        {
            display: 'Edit',
            action: (row) => this.editHat(row),
            color: 'primary',
            disabled: false,
        },
        {
            display: 'Delete',
            action: (row) => this.deleteHat(row),
            color: 'warn',
            disabled: false,
        },
    ];

    public searchableSecurityGroups: DisplayValue[] = [];
    public searchableUsers: DisplayValue[] = [];
    public searchablePolicies: DisplayValue[] = [];
    public searchablePositions: DisplayValue[] = [];

    public dataSource = new MatTableDataSource<OrgHat>([]);
    public displayedColumns = this.columns.map((c) => c.columnDef);
    public tabIndex = 0;
    public isProcessing = false;
    public isEditMode = false;
    public editingHatName = '';
    /** Tracks user avatars that failed to load, keyed by user key */
    public brokenAvatars: Record<string, boolean> = {};

    /** Column sort state */
    public sortColumn = 'positions';
    public sortDirection: 'asc' | 'desc' = 'asc';

    /** Position filter state for Existing Hats tab */
    public hatSearchTerm = '';
    public positionSearchTerm = '';
    public matchedPositionNames: string[] = [];
    public allPositionNames: string[] = [];
    private activeFilters = new Set<string>();
    public allHatsData: OrgHat[] = [];

    /** Shared filter bar configs */
    public filterConfig: OrgFilterConfig[] = [];
    public rightFilterConfig: OrgFilterConfig[] = [];

    /** Minimum spinner display time to avoid flashing */
    private readonly MIN_SPINNER_MS = 1200;

    /** Cleanup subject */
    private destroy$ = new Subject<void>();

    /** Tracks whether we're waiting for backend to process a save */
    private pendingSaveName: string | null = null;

    /** Original assignedToArray when edit mode started — used to detect newly added users */
    private originalAssignedToArray: string[] = [];

    /** Original pendingAssignedToArray when edit mode started — used to detect unticked pending users */
    private originalPendingArray: string[] = [];

    /** Whether the org requires approval for hat assignments */
    public get requireApproval(): boolean {
        return !!(this.data.organization as any)?.settings?.requireHatAssignmentApproval;
    }

    //#endregion

    private cdr = inject(ChangeDetectorRef);
    private dialogService = inject(DialogService);
    private matDialog = inject(MatDialog);
    private profileColorService = inject(ProfileColorService);
    private orgHattingService = inject(OrgHattingService);
    public snackbar = inject(MatSnackBar);
    public obService = inject(OrgBoardService);
    public orgDialogService = inject(OrgDialogService);
    public orgBuilderService = inject(OrgBoardBuilderService);

    constructor(
        public dialogRef: MatDialogRef<OrgNewHatDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: InputData
    ) {
        // Full-bleed on phones (DESIGN.md §Sheets, OE-160); phone-scoped in
        // global-styles.scss, desktop untouched.
        this.dialogRef.addPanelClass('ng-fullbleed-dialog');
    }

    //#region Instantiation
    // Setup\fetches\initialization.

    public ngOnInit(): void {
        this.loadHatsTable();
        this.loadSearchableData();
        this.loadPositions();
        this.applyPreSelections();
        this.subscribeToOrgUpdates();

        // Open on a specific tab if requested (e.g. Existing Hats tab)
        if (this.data.initialTab != null) {
            this.tabIndex = this.data.initialTab;
        }

        // ISS-002: "Assign Hat" from a post used to dump the user on the FULL
        // hats list with no context. When opened straight onto the Existing
        // Hats tab from a specific post, pre-scope the position filter to that
        // post — you land on the hats of the post you came from, and clearing
        // the filter still browses the whole list ("add more hats" stays one
        // click away).
        if (this.tabIndex === 1) {
            const targetPartKey =
                this.data.targetPartKey || this.data.node?.data?.partKey || (this.data.node?.data as any)?.key;
            const orgParts: any[] = this.data.organization?.orgParts || [];
            const part = orgParts.find((p: any) => p.partKey === targetPartKey || p.key === targetPartKey);
            const contextName = part?.name || (this.data.node?.data as any)?.name || '';
            if (contextName) {
                this.positionSearchTerm = contextName;
                this.filterHatsTable();
            }
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Subscribe to live organization updates so the dialog refreshes when backend tasks complete */
    private subscribeToOrgUpdates(): void {
        this.obService.organization$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
            if (!org) return;
            this.data.organization = org;
            this.loadHatsTable();
            this.loadSearchableData();
            this.loadPositions();

            // If we were waiting for a save to be processed, show the success toast now
            if (this.pendingSaveName) {
                const name = this.pendingSaveName;
                this.pendingSaveName = null;
                this.snackbar.open(
                    this.isEditMode
                        ? `Hat "${name}" updated — data refreshed`
                        : `Hat "${name}" created — data refreshed`,
                    'OK',
                    { duration: 3000 }
                );
                this.resetForm();
                this.stopProcessing();
            }

            this.cdr.detectChanges();
        });
    }

    private loadHatsTable(): void {
        const keys: string[] = this.data.organization?.orgHats?.hatsKeys || [];
        const tableData: OrgHat[] = [];
        if (keys)
            keys.forEach((key) => {
                const hat = this.data.organization.orgHats[key];
                // Filter out soft-deleted hats (they may still be in orgHats until backend cleanup runs)
                if (hat && !(hat as any).isSoftDeleted) {
                    tableData.push(hat);
                }
            });
        this.allHatsData = tableData;
        this.allPositionNames = this.getAllHatPositionNames();
        this.buildFilterConfigs();
        this.filterHatsTable();

        // Ensure profile colors are loaded for all users across all hats
        const allUserKeys = new Set<string>();
        tableData.forEach((hat) => {
            const hatData = hat as any;
            const userKeys: string[] = hatData.assignedToArray || hat.userKeys || [];
            userKeys.forEach((k) => allUserKeys.add(k));
            const pendingKeys: string[] = hatData.pendingAssignedToArray || [];
            pendingKeys.forEach((k) => allUserKeys.add(k));
        });
        this.profileColorService.ensureLoadedMany(Array.from(allUserKeys));
    }

    private loadSearchableData(): void {
        // Policies
        const policyKeys = this.data.organization?.orgPolicies?.policiesKeys || [];
        this.searchablePolicies = policyKeys
            .filter((key) => this.data.organization.orgPolicies[key])
            .map((key) => ({
                display: this.data.organization.orgPolicies[key].name,
                value: key,
            }));

        // Users
        const userKeys = this.data.organization?.orgUsers?.userKeys || [];
        this.searchableUsers = userKeys
            .filter((key) => this.data.organization.orgUsers[key])
            .map((key) => ({
                display: this.data.organization.orgUsers[key].name,
                value: key,
            }));

        // Security groups
        const sgKeys = this.data.organization?.orgSecurityGroups?.securityGroupsKeys || [];
        this.searchableSecurityGroups = sgKeys
            .filter((key) => this.data.organization.orgSecurityGroups[key])
            .map((key) => ({
                display: this.data.organization.orgSecurityGroups[key].name,
                value: key,
            }));
    }

    private loadPositions(): void {
        const partsCopy = deepCopy(this.data.organization?.orgParts || []);
        const allPositions: Array<{ display: string; value: string; depth: number }> = [];
        const seenKeys = new Set<string>();

        // Build a parent lookup map from the flat array (using parentPartKey references)
        const partMap = new Map<string, any>();
        for (const part of partsCopy) {
            const pk = part.partKey || part.key;
            if (pk) partMap.set(pk, part);
            // Also index by key if it differs from partKey
            if (part.key && part.key !== pk) partMap.set(part.key, part);
        }

        // Get the immediate parent name for context (e.g. "Unit - dept 19 (Department 19)")
        const getDisplayWithParent = (part: any): string => {
            const name = part.name || part.mainTitle || '';
            const parentKey = part.parentPartKey;
            if (parentKey) {
                const parent = partMap.get(parentKey);
                if (parent) {
                    return `${name} (${parent.name || parent.mainTitle || ''})`;
                }
            }
            return name;
        };

        // Add all flat parts (posts only)
        for (const part of partsCopy) {
            const value = part.partKey || part.key;
            if (!value || seenKeys.has(value)) continue;
            if (part.partType !== 'post') continue;
            seenKeys.add(value);
            allPositions.push({
                display: getDisplayWithParent(part),
                value: value,
                depth: part.depth || 0,
            });
        }

        // Also recurse into nested children in case orgParts has nested structure (posts only)
        const flattenNested = (part: any, parentName: string) => {
            const childTypes = ['sections', 'units', 'posts', 'children'];
            for (const childType of childTypes) {
                if (part[childType] && Array.isArray(part[childType])) {
                    for (const child of part[childType]) {
                        const childKey = child.partKey || child.key;
                        if (childKey && !seenKeys.has(childKey)) {
                            if (child.partType === 'post') {
                                seenKeys.add(childKey);
                                const childName = child.name || child.mainTitle || '';
                                allPositions.push({
                                    display: parentName ? `${childName} (${parentName})` : childName,
                                    value: childKey,
                                    depth: child.depth || 0,
                                });
                            }
                            flattenNested(child, child.name || child.mainTitle || parentName);
                        }
                    }
                }
            }
        };

        for (const part of partsCopy) {
            flattenNested(part, part.name || part.mainTitle || '');
        }

        this.searchablePositions = allPositions
            .sort((a, b) => a.depth - b.depth || a.display.localeCompare(b.display))
            .map((pos) => ({ display: pos.display, value: pos.value }));
    }

    private applyPreSelections(): void {
        // Pre-select position if passed (from drag-drop or context-click flow)
        const targetPartKey = this.data.targetPartKey || this.data.node?.data?.partKey;
        if (targetPartKey) {
            // Normalize: the incoming key might be .key while searchablePositions stores .partKey (or vice versa).
            // Find the matching entry in searchablePositions to use the correct value.
            let resolvedKey = targetPartKey;
            const directMatch = this.searchablePositions.find((p) => p.value === targetPartKey);
            if (!directMatch) {
                // Try finding in orgParts by either key or partKey, then use whichever ID searchablePositions has
                const orgParts = this.data.organization?.orgParts || [];
                const matchingPart = orgParts.find((p) => p.partKey === targetPartKey || p.key === targetPartKey);
                if (matchingPart) {
                    const altKey = matchingPart.partKey || matchingPart.key;
                    const altMatch = this.searchablePositions.find((p) => p.value === altKey);
                    if (altMatch) resolvedKey = altKey;
                }
            }
            this.form.patchValue({ positionArray: [resolvedKey] });
        }

        // Pre-select user if passed (from drag-drop flow)
        if (this.data.preSelectedUser) {
            this.form.patchValue({ assignedToArray: [this.data.preSelectedUser.uid] });
        }
    }

    //#endregion
    //#region User Interactions
    // Callbacks from html, anything the user can "interact" with from the view.

    public async saveHat(): Promise<void> {
        if (this.form.invalid || this.isProcessing) return;

        this.startProcessing();
        const formValue = this.form.value;

        try {
            // Detect newly added users (only relevant in edit mode with approval enabled)
            let newlyAddedUsers: string[] = [];
            const allFormUsers = formValue.assignedToArray || [];

            if (this.isEditMode && this.requireApproval) {
                newlyAddedUsers = allFormUsers.filter(
                    (uid: string) =>
                        !this.originalAssignedToArray.includes(uid) && !this.originalPendingArray.includes(uid)
                );
            }

            // Detect pending users that were unticked (need to be removed from pending)
            const removedPendingUsers =
                this.isEditMode && this.requireApproval
                    ? this.originalPendingArray.filter((uid: string) => !allFormUsers.includes(uid))
                    : [];

            // Users to write to assignedToArray: exclude newly added AND still-pending users
            const usersForAssigned =
                this.isEditMode && this.requireApproval
                    ? allFormUsers.filter(
                          (uid: string) => !newlyAddedUsers.includes(uid) && !this.originalPendingArray.includes(uid)
                      )
                    : allFormUsers;

            // Compute pendingAssignedToArray for single-write: original pending - removed + newly added
            const pendingForSave =
                this.isEditMode && this.requireApproval
                    ? [
                          ...this.originalPendingArray.filter((uid: string) => !removedPendingUsers.includes(uid)),
                          ...newlyAddedUsers,
                      ]
                    : undefined;

            // Build hat data with both interface and flat-array fields for backend compatibility
            const hatData: Partial<OrgHat> & Record<string, any> = {
                description: formValue.description || '',
                isMultiUsers: formValue.isMultiUsers ?? false,
                isSpecialRights: formValue.isSpecialRights ?? false,
                name: formValue.name || '',
                userKeys: usersForAssigned,
                policyKeys: this.transformPoliciesToPolicyKeys(formValue.policies || []),
                positions: this.transformPositionArrayToPositions(formValue.positionArray || []),
                securityGroups: this.transformSecurityGroupsArrayToSecurityGroups(formValue.securityGroupsArray || []),
                // IMPORTANT: Store as plain string keys for backend array-contains query compatibility
                securityGroupsArray: formValue.securityGroupsArray || [],
                // Flat arrays used by backend processing
                positionArray: formValue.positionArray || [],
                assignedToArray: usersForAssigned,
                assigned: usersForAssigned.length > 0,
                assignedTo: usersForAssigned[0] || '',
                multiUsers: formValue.isMultiUsers ?? false,
                policies: formValue.policies || [],
            };

            // Include pendingAssignedToArray in the same write to avoid double hat-doc writes
            if (pendingForSave !== undefined) {
                hatData.pendingAssignedToArray = pendingForSave;
            }

            if (formValue.key) {
                hatData.key = formValue.key;
            }

            const saveResult = await this.obService.saveHat(hatData as unknown as OrgHat);

            if (saveResult.succeeded) {
                // Route newly added users through approval workflow (skip hat doc — already written above)
                if (newlyAddedUsers.length > 0 && formValue.key) {
                    const orgId = this.data.organization.key;
                    const positionKeys: string[] = formValue.positionArray || [];
                    const positionKey = positionKeys.length > 0 ? positionKeys[0] : null;
                    const positionName = positionKey
                        ? this.searchablePositions.find((p) => p.value === positionKey)?.display || null
                        : null;

                    await this.orgHattingService.addUsersPendingApproval(
                        orgId,
                        formValue.key,
                        newlyAddedUsers,
                        positionKey,
                        positionName,
                        true // skipHatDocUpdate — hat already saved with pendingAssignedToArray
                    );
                }

                // Remove unticked pending users (skip hat doc — already written above)
                if (removedPendingUsers.length > 0 && formValue.key) {
                    const orgId = this.data.organization.key;
                    await this.orgHattingService.removeUsersPendingApproval(
                        orgId,
                        formValue.key,
                        removedPendingUsers,
                        true // skipHatDocUpdate — hat already saved with pendingAssignedToArray
                    );
                }

                // Don't show final toast yet — wait for org$ to emit with refreshed data
                this.pendingSaveName = formValue.name || '';
                this.snackbar.open('Saving... waiting for backend to process', undefined, { duration: 8000 });
                // Keep spinner running — it will be stopped when org$ emits
                // Set a safety timeout in case the org$ update never arrives
                setTimeout(() => {
                    if (this.pendingSaveName) {
                        this.pendingSaveName = null;
                        this.snackbar.open(
                            this.isEditMode
                                ? `Hat "${formValue.name}" saved (refresh may be delayed)`
                                : `Hat "${formValue.name}" created (refresh may be delayed)`,
                            'OK',
                            { duration: 4000 }
                        );
                        this.resetForm();
                        this.loadHatsTable();
                        this.stopProcessing();
                    }
                }, 15000);
                return; // Don't stop processing here — org$ or timeout will handle it
            }
        } catch (err) {
            this.snackbar.open('Failed to save hat. Please try again.', 'Dismiss', { duration: 5000 });
        } finally {
            // Only stop processing if we're NOT waiting for org$ (i.e., save failed)
            if (!this.pendingSaveName) {
                this.stopProcessing();
            }
        }
    }

    public viewHat(row: OrgHat): void {
        this.matDialog.open(OrgHatDetailsDialogComponent, {
            data: {
                hat: row,
                hatKey: row.key,
                organization: this.data.organization,
            },
            height: '85%',
            width: '85%',
            maxHeight: 'unset',
            maxWidth: 'unset',
        });
    }

    public editHat(row: OrgHat): void {
        this.form.reset();

        // Normalize the hat data — field names vary between interface and stored data
        const hatData = row as OrgHat & {
            assignedToArray?: string[];
            assignedTo?: string;
            policies?: string[];
            positionArray?: string[];
        };

        const parsed = {
            key: row.key,
            name: row.name,
            description: row.description || '',
            isMultiUsers: row.isMultiUsers ?? false,
            isSpecialRights: row.isSpecialRights ?? false,
            securityGroupsArray: row.securityGroupsArray?.map((c) => (typeof c === 'string' ? c : c.key)) || [],
            assignedToArray:
                hatData.assignedToArray || row.userKeys || (hatData.assignedTo ? [hatData.assignedTo] : []),
            policies: row.policyKeys ? Object.keys(row.policyKeys) : hatData.policies || [],
            positionArray: hatData.positionArray || row.positions?.map((p: Positions) => p.key || p.partKey) || [],
        };

        // Filter to only values that exist in the searchable lists
        const validUsers = parsed.assignedToArray.filter((k) => this.searchableUsers.some((u) => u.value === k));
        const validSGs = parsed.securityGroupsArray.filter((k) =>
            this.searchableSecurityGroups.some((sg) => sg.value === k)
        );
        const validPolicies = parsed.policies.filter((k) => this.searchablePolicies.some((p) => p.value === k));
        const validPositions = parsed.positionArray.filter((k) =>
            this.searchablePositions.some((pos) => pos.value === k)
        );

        this.form.patchValue({
            ...parsed,
            assignedToArray: validUsers,
            securityGroupsArray: validSGs,
            policies: validPolicies,
            positionArray: validPositions,
        });

        // Set multi-select control values after patchValue, then trigger change detection
        this.usersControl.setValue(validUsers);
        this.securityGroupsControl.setValue(validSGs);
        this.policiesControl.setValue(validPolicies);
        this.positionControl.setValue(validPositions);
        this.cdr.detectChanges();

        this.isEditMode = true;
        this.editingHatName = row.name || '';
        this.tabIndex = 0;

        // Capture original assigned users for diff-ing on save (to detect newly added)
        this.originalAssignedToArray = [...validUsers];

        // Include pending users in the form control so they appear ticked in the dropdown
        const pendingKeys: string[] = (hatData as any).pendingAssignedToArray || [];
        const validPending = pendingKeys.filter(
            (k: string) => !validUsers.includes(k) && this.searchableUsers.some((u) => u.value === k)
        );
        this.originalPendingArray = [...validPending];

        // Tag pending users in the searchable list so they render with a pending style in the dropdown
        this.searchableUsers = this.searchableUsers.map((u) => {
            const item = { ...u } as any;
            if (validPending.includes(u.value)) {
                item.cssClass = 'option-pending';
            } else {
                delete item.cssClass;
            }
            return item;
        });

        // Merge approved + pending into form control
        const mergedUsers = [...validUsers, ...validPending];
        this.usersControl.setValue(mergedUsers);
        this.form.patchValue({ assignedToArray: mergedUsers });
        this.cdr.detectChanges();
    }

    public async deleteHat(row: OrgHat): Promise<void> {
        const confirmed = await this.dialogService.asyncConfirm(
            'Delete Hat',
            `Are you sure you want to delete the hat "${row.name}"? This action cannot be undone.`
        );

        if (!confirmed) return;

        this.startProcessing();
        try {
            const hatData = { ...row, isSoftDeleted: true } as OrgHat;
            const result = await this.obService.saveHat(hatData);
            if (result.succeeded) {
                this.snackbar.open(`Hat "${row.name}" deleted`, 'OK', { duration: 3000 });
                // Remove from local table
                this.dataSource.data = this.dataSource.data.filter((h) => h.key !== row.key);
            }
        } catch (err) {
            this.snackbar.open('Failed to delete hat. Please try again.', 'Dismiss', { duration: 5000 });
        } finally {
            this.stopProcessing();
        }
    }

    public openCleanupDialog(): void {
        const ref = this.matDialog.open(OrgHatCleanupDialogComponent, {
            width: '1200px',
            height: '80vh',
            data: { organization: this.data.organization },
        });
        ref.afterClosed().subscribe(() => {
            // Refresh local hats table in case hats were deleted or modified
            this.loadHatsTable();
        });
    }

    public resetForm(): void {
        this.form.reset({ isMultiUsers: false, isSpecialRights: false, assignedToArray: [] });
        this.isEditMode = false;
        this.editingHatName = '';
        this.originalAssignedToArray = [];
        this.originalPendingArray = [];

        // Clear any pending tags from searchable users list
        this.searchableUsers = this.searchableUsers.map((u) => {
            const item = { ...u } as any;
            delete item.cssClass;
            return item;
        });
        this.loadSearchableData();
    }

    public openPart(): void {
        this.orgDialogService.openSelectPositionDialog(this.data.node).then((selectedPosition) => {
            if (!selectedPosition?.node?.data) return;

            const partKey = selectedPosition.node.data.partKey;
            const partName = selectedPosition.node.data.name;

            // Add to searchable list if new
            if (!this.searchablePositions.find((p) => p.value === partKey)) {
                this.searchablePositions = [...this.searchablePositions, { display: partName, value: partKey }];
            }

            // Add to current selection (multi-select)
            const current = this.positionControl.value || [];
            if (!current.includes(partKey)) {
                this.positionControl.setValue([...current, partKey]);
            }
            this.form.markAsDirty();
        });
    }

    public newSecurityGroup(): void {
        this.orgDialogService.openSecurityGroupDialog(true).then((saveResult) => {
            if (!saveResult?.succeeded || !saveResult.entity) return;

            const sg = saveResult.entity;
            // Add new SG to dropdown so it's immediately selectable
            if (sg.key && !this.searchableSecurityGroups.find((s) => s.value === sg.key)) {
                this.searchableSecurityGroups = [...this.searchableSecurityGroups, { display: sg.name, value: sg.key }];
            }

            // Auto-select the newly created SG
            if (sg.key) {
                const current = this.securityGroupsControl.value || [];
                if (!current.includes(sg.key)) {
                    this.securityGroupsControl.setValue([...current, sg.key]);
                }
                this.form.markAsDirty();
            }

            this.snackbar.open(`Security Group "${sg.name}" created and selected`, 'OK', { duration: 3000 });
        });
    }

    //#endregion
    //#region Events and Domain Functions
    // $watches/$on events, functions which don't fall into any of the other regions,
    // including any function that has to do domain functionality.

    // eslint-disable-next-line @typescript-eslint/no-empty-function -- bound as (selected) on the policy searchable-select in the template; intentionally a no-op placeholder (nothing to react to on selection today)
    public onSelectPolicy(_$event: DisplayValue | DisplayValue[]): void {}
    public onSelectUser(_$event: DisplayValue | DisplayValue[]): void {
        this.updatePendingTags();
    }

    /** Re-tags searchableUsers with pending CSS class based on current form state */
    private updatePendingTags(): void {
        if (!this.isEditMode || !this.requireApproval) return;
        const currentValues: string[] = this.usersControl.value || [];
        this.searchableUsers = this.searchableUsers.map((u) => {
            const item = { ...u } as any;
            const isNew =
                currentValues.includes(u.value) &&
                !this.originalAssignedToArray.includes(u.value) &&
                !this.originalPendingArray.includes(u.value);
            if (isNew || this.originalPendingArray.includes(u.value)) {
                item.cssClass = 'option-pending';
            } else {
                delete item.cssClass;
            }
            return item;
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- bound as (selected) on the SG searchable-select in the template; intentionally a no-op placeholder (nothing to react to on selection today)
    public onSelectSG(_$event: DisplayValue | DisplayValue[]): void {}
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- bound as (selected) on the position searchable-select in the template; intentionally a no-op placeholder (nothing to react to on selection today)
    public onSelectPosition(_$event: DisplayValue | DisplayValue[]): void {}

    /** Opens the Hat Explorer dialog */
    public openHatExplorer(): void {
        this.matDialog.open(OrgHatExplorerDialogComponent, {
            width: '80vw',
            height: '80vh',
            maxWidth: '1200px',
            maxHeight: '800px',
            data: {
                organization: this.data.organization,
            },
        });
    }

    // ── Item Selector Dialog Methods ──

    /** Opens the unified item selector and applies the result to the given form control */
    private openItemSelector(
        type: ItemSelectorType,
        control: FormControl,
        sourceList: DisplayValue[],
        overrides?: { title?: string; subtitle?: string }
    ): void {
        // Compute pending keys for the user selector
        const pendingKeys =
            type === 'users' && this.isEditMode && this.requireApproval ? this.getCurrentPendingKeys() : undefined;
        const approvedKeys =
            type === 'users' && this.isEditMode && this.requireApproval ? [...this.originalAssignedToArray] : undefined;

        const dialogRef = this.matDialog.open(OrgItemSelectorDialogComponent, {
            width: '70vw',
            height: '80vh',
            maxWidth: '900px',
            maxHeight: '800px',
            panelClass: 'item-selector-panel',
            data: {
                organization: this.data.organization,
                type,
                selectedKeys: control.value || [],
                pendingKeys,
                approvedKeys,
                title: overrides?.title,
                subtitle: overrides?.subtitle,
            },
        });

        dialogRef.afterClosed().subscribe((result: ItemSelectorDialogResult | null) => {
            if (!result) return;
            control.setValue(result.selectedKeys);
            this.form.markAsDirty();

            // If positions, also ensure new items appear in the searchable list
            if (type === 'positions') {
                for (const item of result.selectedItems) {
                    if (!this.searchablePositions.find((p) => p.value === item.value)) {
                        this.searchablePositions = [...this.searchablePositions, item];
                    }
                }
            }

            // If users and approval is active, tag new selections as pending
            if (type === 'users') {
                this.updatePendingTags();
            }

            this.cdr.detectChanges();
        });
    }

    /** Opens the positions tree selector (posts only) */
    public openPositionSelector(): void {
        this.openItemSelector('positions', this.positionControl, this.searchablePositions, {
            title: 'Select Org Posts',
            subtitle: 'Choose org posts from the organization structure',
        });
    }

    /** Opens the security groups list selector */
    public openSecurityGroupSelector(): void {
        this.openItemSelector('security-groups', this.securityGroupsControl, this.searchableSecurityGroups);
    }

    /** Opens the policies list selector */
    public openPolicySelector(): void {
        this.openItemSelector('policies', this.policiesControl, this.searchablePolicies);
    }

    /** Opens the users list selector */
    public openUserSelector(): void {
        this.openItemSelector('users', this.usersControl, this.searchableUsers);
    }

    /** Opens the Security Group details dialog (read-only) for a selected SG */
    public viewSecurityGroup(sgKey: string): void {
        const sg = this.data.organization?.orgSecurityGroups?.[sgKey];
        if (!sg) {
            this.snackbar.open('Security group not found', 'Dismiss', { duration: 3000 });
            return;
        }

        this.matDialog.open(OrgSecurityGroupDetailsDialogComponent, {
            width: '85vw',
            height: '85vh',
            maxWidth: '1400px',
            maxHeight: '900px',
            data: {
                securityGroup: sg,
                securityGroupKey: sgKey,
                organization: this.data.organization,
            },
            panelClass: 'security-group-details-dialog',
        });
    }

    /** Opens the Position details dialog (read-only) for a selected position */
    public viewPosition(partKey: string): void {
        const orgParts = this.data.organization?.orgParts || [];
        // Search the flat orgParts array for a matching part
        const position = orgParts.find((p) => (p.partKey || p.key) === partKey);
        if (!position) {
            this.snackbar.open('Position not found', 'Dismiss', { duration: 3000 });
            return;
        }

        this.matDialog.open(OrgPositionDetailsDialogComponent, {
            height: '85%',
            width: '85%',
            maxHeight: 'unset',
            maxWidth: 'unset',
            data: {
                organization: this.data.organization,
                position: position,
            },
        });
    }

    /** Opens the Policy details dialog (read-only) for a selected policy */
    public viewPolicy(policyKey: string): void {
        const policy = this.data.organization?.orgPolicies?.[policyKey];
        if (!policy) {
            this.snackbar.open('Policy not found', 'Dismiss', { duration: 3000 });
            return;
        }

        this.matDialog.open(OrgPolicyDetailsDialogComponent, {
            width: '85vw',
            height: '85vh',
            maxWidth: '1400px',
            maxHeight: '900px',
            data: {
                policy: policy,
                policyKey: policyKey,
                organization: this.data.organization,
            },
            panelClass: 'policy-details-dialog',
        });
    }

    /** Opens the User details dialog (read-only) for a selected user */
    public viewUser(userKey: string): void {
        const orgUser = this.data.organization?.orgUsers?.[userKey];
        if (!orgUser) {
            this.snackbar.open('User not found', 'Dismiss', { duration: 3000 });
            return;
        }

        const userForDialog = {
            ...orgUser,
            key: userKey,
            name: orgUser.name || '',
        };

        this.matDialog.open(OrgUserDetailsDialogComponent, {
            height: '85%',
            width: '85%',
            maxHeight: 'unset',
            maxWidth: 'unset',
            data: {
                node: null,
                user: userForDialog,
                organization: this.data.organization,
                onlineUserIds: this.orgDialogService.onlineUserIds,
            },
        });
    }

    private buildFilterConfigs(): void {
        const hats = this.allHatsData;
        this.filterConfig = [
            {
                key: 'positioned',
                label: 'Positioned',
                icon: 'account_tree',
                count: hats.filter((h) => !this.isPositionless(h)).length,
            },
            {
                key: 'posts',
                label: 'Posts',
                icon: 'local_post_office',
                count: hats.filter((h) => this.isHatLinkedToPost(h)).length,
            },
            {
                key: 'specialRights',
                label: 'Special Rights',
                icon: 'verified_user',
                count: hats.filter((h) => h.isSpecialRights === true).length,
            },
            {
                key: 'withUsers',
                label: 'With Staff',
                icon: 'people',
                count: hats.filter((h) => this.getUsersForHat(h).length > 0).length,
            },
            {
                key: 'noUsers',
                label: 'No Staff',
                icon: 'people_outline',
                count: hats.filter((h) => this.getUsersForHat(h).length === 0).length,
            },
            {
                key: 'withSg',
                label: 'With Groups',
                icon: 'security',
                count: hats.filter((h) => this.getSecurityGroupNames(h).length > 0).length,
            },
            {
                key: 'noSg',
                label: 'No Groups',
                icon: 'shield',
                count: hats.filter((h) => this.getSecurityGroupNames(h).length === 0).length,
            },
            {
                key: 'pendingApprovals',
                label: 'Pending Approvals',
                icon: 'hourglass_empty',
                count: hats.filter((h) => this.getPendingUsersForHat(h).length > 0).length,
            },
        ];
    }

    public onSearchChanged(term: string): void {
        this.hatSearchTerm = term;
        this.filterHatsTable();
    }

    public onPositionSearchChanged(result: PositionSearchResult): void {
        this.positionSearchTerm = result.term;
        this.matchedPositionNames = result.matchedNames;
        this.filterHatsTable();
    }

    public onFiltersChanged(filters: Set<string>): void {
        this.activeFilters = filters;
        this.filterHatsTable();
    }

    private filterHatsTable(): void {
        let result = [...this.allHatsData];
        // Clear any MatTableDataSource built-in filter
        this.dataSource.filter = '';

        // Text search on hat name, description, user names, position names, and security group names
        if (this.hatSearchTerm) {
            const term = this.hatSearchTerm.toLowerCase();
            result = result.filter(
                (hat) =>
                    (hat.name || '').toLowerCase().includes(term) ||
                    (hat.description || '').toLowerCase().includes(term) ||
                    this.getUsersForHat(hat).some((u) => (u.name || '').toLowerCase().includes(term)) ||
                    this.getPendingUsersForHat(hat).some((u) => (u.name || '').toLowerCase().includes(term)) ||
                    this.resolveHatPositionNames(hat).some((p) => p.toLowerCase().includes(term)) ||
                    this.getSecurityGroupNames(hat).some((sg) => sg.toLowerCase().includes(term))
            );
        }

        // Pill filter: scope filters
        if (this.activeFilters.has('positioned')) {
            result = result.filter((hat) => !this.isPositionless(hat));
        }
        if (this.activeFilters.has('specialRights')) {
            result = result.filter((hat) => hat.isSpecialRights === true);
        }
        if (this.activeFilters.has('posts')) {
            result = result.filter((hat) => this.isHatLinkedToPost(hat));
        }

        // Pill filter: users
        if (this.activeFilters.has('withUsers')) {
            result = result.filter((hat) => this.getUsersForHat(hat).length > 0);
        }
        if (this.activeFilters.has('noUsers')) {
            result = result.filter((hat) => this.getUsersForHat(hat).length === 0);
        }

        // Pill filter: security groups
        if (this.activeFilters.has('withSg')) {
            result = result.filter((hat) => this.getSecurityGroupNames(hat).length > 0);
        }
        if (this.activeFilters.has('noSg')) {
            result = result.filter((hat) => this.getSecurityGroupNames(hat).length === 0);
        }

        // Pill filter: pending approvals
        if (this.activeFilters.has('pendingApprovals')) {
            result = result.filter((hat) => this.getPendingUsersForHat(hat).length > 0);
        }

        // Position search (fuzzy)
        if (this.positionSearchTerm.trim()) {
            const posTerm = this.positionSearchTerm.toLowerCase().trim();
            result = result.filter((hat) => {
                const posNames = this.resolveHatPositionNames(hat);
                return posNames.some((pos) => fuzzyMatch(pos.toLowerCase(), posTerm));
            });
        }

        // Apply column sort
        this.applySorting(result);

        this.dataSource.data = result;
    }

    public toggleSort(column: string): void {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
        this.filterHatsTable();
    }

    private applySorting(result: OrgHat[]): void {
        const dir = this.sortDirection === 'asc' ? 1 : -1;

        if (this.sortColumn === 'positions') {
            // Scope sort: hierarchy order with posts grouped
            const orgParts = this.data.organization?.orgParts || [];
            const partMap = new Map<string, any>();
            for (const part of orgParts) {
                const key = part.partKey || part.key;
                if (key) partMap.set(key, part);
            }

            const getPostSortKey = (hat: OrgHat): string => {
                const hatData = hat as any;
                const posKeys: string[] =
                    hatData.positionArray || hat.positions?.map((p: any) => p.key || p.partKey) || [];
                if (posKeys.length === 0) return '\uffff';
                const firstKey = posKeys[0];
                const part = partMap.get(firstKey);
                if (!part) return firstKey;
                const chain: string[] = [];
                let current = part;
                while (current) {
                    chain.unshift((current.name || current.mainTitle || '').toLowerCase());
                    current = current.parentPartKey ? partMap.get(current.parentPartKey) : null;
                }
                return chain.join('\x00');
            };

            result.sort((a, b) => {
                const cmp = getPostSortKey(a).localeCompare(getPostSortKey(b));
                if (cmp !== 0) return cmp * dir;
                return (a.name || '').localeCompare(b.name || '') * dir;
            });
        } else if (this.sortColumn === 'name') {
            result.sort((a, b) => (a.name || '').localeCompare(b.name || '') * dir);
        } else if (this.sortColumn === 'users') {
            result.sort((a, b) => {
                const aCount = this.getUsersForHat(a).length;
                const bCount = this.getUsersForHat(b).length;
                return (aCount - bCount) * dir || (a.name || '').localeCompare(b.name || '');
            });
        } else if (this.sortColumn === 'securityGroups') {
            result.sort((a, b) => {
                const aCount = this.getSecurityGroupNames(a).length;
                const bCount = this.getSecurityGroupNames(b).length;
                return (aCount - bCount) * dir || (a.name || '').localeCompare(b.name || '');
            });
        }
    }

    private resolveHatPositionNames(hat: OrgHat): string[] {
        const names: string[] = [];
        const orgParts = this.data.organization?.orgParts || [];
        const hatData = hat as any;

        if (hatData.positions && Array.isArray(hatData.positions)) {
            hatData.positions.forEach((pos: any) => {
                if (pos?.name && !names.includes(pos.name)) names.push(pos.name);
            });
        }

        if (hatData.positionArray && Array.isArray(hatData.positionArray)) {
            hatData.positionArray.forEach((partKey: string) => {
                const part = orgParts.find((p: any) => p.key === partKey || p.partKey === partKey);
                if (part?.name && !names.includes(part.name)) names.push(part.name);
            });
        }

        return names;
    }

    private getAllHatPositionNames(): string[] {
        const names = new Set<string>();
        for (const hat of this.allHatsData) {
            for (const pos of this.resolveHatPositionNames(hat)) {
                names.add(pos);
            }
        }
        return Array.from(names).sort();
    }

    /** Returns the display items for currently selected values in a multi-select control */
    public getSelectedItems(control: FormControl, sourceList: DisplayValue[]): DisplayValue[] {
        const selected: string[] = control.value || [];
        if (!selected.length) return [];
        return selected
            .map((val) => sourceList.find((item) => item.value === val))
            .filter((item): item is DisplayValue => !!item);
    }

    /** Removes a value from a multi-select control (used by chip remove buttons) */
    public removeSelection(control: FormControl, value: string): void {
        const current: string[] = control.value || [];
        control.setValue(current.filter((v) => v !== value));
        this.form.markAsDirty();
    }

    /** Returns approved user chip items (excludes pending and newly added users) */
    public getApprovedUserChips(): DisplayValue[] {
        const all = this.getSelectedItems(this.usersControl, this.searchableUsers);
        if (!this.isEditMode || !this.requireApproval) return all;
        return all.filter((item) => this.originalAssignedToArray.includes(item.value));
    }

    /** Returns pending user chip items (existing pending + newly ticked users needing approval) */
    public getPendingUserChips(): DisplayValue[] {
        if (!this.isEditMode || !this.requireApproval) return [];
        const all = this.getSelectedItems(this.usersControl, this.searchableUsers);
        return all.filter((item) => !this.originalAssignedToArray.includes(item.value));
    }

    /** Returns all user keys that are currently pending (original pending + newly ticked) */
    private getCurrentPendingKeys(): string[] {
        const currentValues: string[] = this.usersControl.value || [];
        return currentValues.filter((uid) => !this.originalAssignedToArray.includes(uid));
    }

    //#endregion
    //#region Getters and Filters
    // Getter functions and filter methods/functions.

    public get columnsWithoutActions(): DynamicTableColumn[] {
        return this.columns.filter((c) => c.columnDef !== 'actions');
    }

    /** Returns resolved user objects for a hat row (for stacked avatars) */
    public getUsersForHat(
        hat: OrgHat
    ): { key: string; name: string; photoURL?: string; photoThumbnail?: string; profileColor?: string }[] {
        const hatData = hat as any;
        const userKeys: string[] = hatData.assignedToArray || hat.userKeys || [];
        const orgUsers = this.data.organization?.orgUsers;
        if (!orgUsers) return [];
        return userKeys
            .filter((k) => orgUsers[k])
            .map((k) => ({
                key: k,
                name: orgUsers[k].name || orgUsers[k].userName || k,
                photoURL: orgUsers[k].photoURL,
                photoThumbnail: orgUsers[k].photoThumbnail,
                profileColor: this.profileColorService.getColor(k),
            }));
    }

    /** Returns pending (unapproved) user objects for a hat row */
    public getPendingUsersForHat(
        hat: OrgHat
    ): { key: string; name: string; photoURL?: string; photoThumbnail?: string; profileColor?: string }[] {
        const hatData = hat as any;
        const approvedKeys: string[] = hatData.assignedToArray || hat.userKeys || [];
        const pendingKeys: string[] = hatData.pendingAssignedToArray || [];
        // Only show users not already approved
        const uniquePendingKeys = pendingKeys.filter((k) => !approvedKeys.includes(k));
        const orgUsers = this.data.organization?.orgUsers;
        if (!orgUsers || uniquePendingKeys.length === 0) return [];
        return uniquePendingKeys
            .filter((k) => orgUsers[k])
            .map((k) => ({
                key: k,
                name: orgUsers[k].name || orgUsers[k].userName || k,
                photoURL: orgUsers[k].photoURL,
                photoThumbnail: orgUsers[k].photoThumbnail,
                profileColor: this.profileColorService.getColor(k),
            }));
    }

    /** Returns tooltip string of user names for a hat */
    public getUserNamesTooltip(hat: OrgHat): string {
        const approved = this.getUsersForHat(hat).map((u) => u.name);
        const pending = this.getPendingUsersForHat(hat).map((u) => `${u.name} (pending)`);
        return [...approved, ...pending].join(', ');
    }

    /** Returns initials from a name (e.g. "John Doe" → "JD") */
    public getInitials(name: string): string {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        return parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : name.substring(0, 2).toUpperCase();
    }

    /** Returns whether a hat is linked to a post-type position */
    public isHatLinkedToPost(hat: OrgHat): boolean {
        const hatData = hat as any;
        if (!hatData.positionArray || !Array.isArray(hatData.positionArray)) return false;
        const orgParts = this.data.organization?.orgParts || [];
        return hatData.positionArray.some((partKey: string) => {
            const part = orgParts.find((p: any) => p.key === partKey || p.partKey === partKey);
            if (!part) return false;
            return this.resolvePartType(part) === 'post';
        });
    }

    /** Resolve part type: use partType if set, else infer from depth */
    private resolvePartType(part: OrgPart): string {
        if (part.partType) return part.partType.toLowerCase();
        const depth = part.depth || 0;
        if (depth <= 1) return 'executive';
        if (depth === 2) return 'division';
        if (depth === 3) return 'department';
        if (depth === 4) return 'section';
        if (depth === 5) return 'unit';
        if (depth >= 6) return 'post';
        return '';
    }

    /** Returns whether a hat has no positions (org-wide / positionless) */
    public isPositionless(hat: OrgHat): boolean {
        const hatData = hat as any;
        const positions = hatData.positionArray || hat.positions?.map((p: any) => p.key || p.partKey) || [];
        return positions.length === 0;
    }

    /** Returns position names for a hat row (short names for inline display) */
    public getPositionNames(hat: OrgHat): string[] {
        const hatData = hat as any;
        const positionKeys: string[] =
            hatData.positionArray || hat.positions?.map((p: any) => p.key || p.partKey) || [];
        const orgParts = this.data.organization?.orgParts || [];
        return positionKeys.map((k) => {
            const part = orgParts.find((p) => (p.partKey || p.key) === k);
            return part?.name || part?.mainTitle || k;
        });
    }

    /** Returns formatted tooltip text for a hat's positions (position name + origin path) */
    public getPositionTooltip(hat: OrgHat): string {
        const hatData = hat as any;
        const positionKeys: string[] =
            hatData.positionArray || hat.positions?.map((p: any) => p.key || p.partKey) || [];
        const orgParts = this.data.organization?.orgParts || [];

        // Build a lookup map for resolving parent chains
        const partMap = new Map<string, any>();
        for (const part of orgParts) {
            const key = part.partKey || part.key;
            if (key) partMap.set(key, part);
        }

        const lines: string[] = [];
        positionKeys.forEach((k, idx) => {
            const positionPart = partMap.get(k);
            const posName = positionPart?.name || positionPart?.mainTitle || k;

            // Build origin path (parent chain excluding the position itself)
            const originSegments: string[] = [];
            if (positionPart) {
                let parent = positionPart.parentPartKey ? partMap.get(positionPart.parentPartKey) : null;
                while (parent) {
                    originSegments.unshift(parent.name || parent.mainTitle || parent.partKey || parent.key);
                    const parentKey = parent.parentPartKey;
                    parent = parentKey ? partMap.get(parentKey) : null;
                }
            }

            if (idx > 0) lines.push(''); // blank line separator between positions
            lines.push(`📍 ${posName}`);
            if (originSegments.length > 0) {
                lines.push(`   └ ${originSegments.join(' › ')}`);
            }
        });
        return lines.join('\n');
    }

    /** Returns resolved security group names for a hat row */
    public getSecurityGroupNames(hat: OrgHat): string[] {
        const sgKeys: string[] = (hat.securityGroupsArray || (hat as any).securityGroups || []).map((sg: any) =>
            typeof sg === 'string' ? sg : sg.key
        );
        const orgSGs = this.data.organization?.orgSecurityGroups;
        if (!orgSGs) return [];
        return sgKeys.filter((k) => orgSGs[k]).map((k) => orgSGs[k].name || k);
    }

    public get positionControl(): FormControl {
        return this.form.get('positionArray') as FormControl;
    }

    public get policiesControl(): FormControl {
        return this.form.get('policies') as FormControl;
    }

    public get usersControl(): FormControl {
        return this.form.get('assignedToArray') as FormControl;
    }

    public get securityGroupsControl(): FormControl {
        return this.form.get('securityGroupsArray') as FormControl;
    }

    /** Dynamic dialog title based on create/edit mode */
    public get dialogTitle(): string {
        return this.isEditMode ? `Edit Hat` : 'New Hat';
    }

    /** Subtitle showing what's being edited */
    public get dialogSubtitle(): string {
        return this.isEditMode ? this.editingHatName : '';
    }

    /** Quick stats for the form header */
    public get formStats(): { users: number; sgs: number; positions: number; policies: number } {
        return {
            users: this.usersControl.value?.length || 0,
            sgs: this.securityGroupsControl.value?.length || 0,
            positions: this.positionControl.value?.length || 0,
            policies: this.policiesControl.value?.length || 0,
        };
    }

    //#endregion
    //#region Processing State
    // Spinner management with minimum display time

    private processingStartTime = 0;

    private startProcessing(): void {
        this.isProcessing = true;
        this.processingStartTime = Date.now();
    }

    private async stopProcessing(): Promise<void> {
        const elapsed = Date.now() - this.processingStartTime;
        if (elapsed < this.MIN_SPINNER_MS) {
            await new Promise((resolve) => setTimeout(resolve, this.MIN_SPINNER_MS - elapsed));
        }
        this.isProcessing = false;
    }

    //#endregion
    //#region Data Transformation Helpers
    // Helper methods to transform form data to match OrgHat interface

    private transformPoliciesToPolicyKeys(policies: string[]): Record<string, string> {
        const policyKeys: Record<string, string> = {};
        policies.forEach((key) => {
            policyKeys[key] = key;
        });
        return policyKeys;
    }

    private transformPositionArrayToPositions(positionArray: string[]): Positions[] {
        return positionArray.map((positionKey) => {
            const match = this.searchablePositions.find((p) => p.value === positionKey);
            return {
                key: positionKey,
                partKey: positionKey,
                name: match?.display || '',
                colour: { background: '', color: '', linkColor: '' },
                depth: 0,
                id: 0,
                parentPartKey: '',
                parentRef: '',
                path: '',
            } as Positions;
        });
    }

    private transformSecurityGroupsArrayToSecurityGroups(securityGroupsArray: string[]): OrgSecurityGroup[] {
        return securityGroupsArray.map((sgKey) => {
            const match = this.searchableSecurityGroups.find((sg) => sg.value === sgKey);
            return { key: sgKey, name: match?.display || '' } as OrgSecurityGroup;
        });
    }

    //#endregion
}

interface InputData {
    /** The organization the hat is being created for. */
    organization: Organization;

    /** Possible node that has the position this hat is being created for. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    node?: ECONode<any>;

    /** Pre-selected user to assign to the hat (from drag-drop flow). */
    preSelectedUser?: { uid: string; name: string };

    /**
     * Explicit target position partKey (for nested parts like sections/units within a department).
     * Takes precedence over node.data.partKey if provided.
     */
    targetPartKey?: string;

    /** Initial tab index to show when the dialog opens (0 = Create/Edit, 1 = Existing Hats). */
    initialTab?: number;
}
